/**
 * Copyright (c) 2020 Karmabunny
 */

// Cache thing.
const FORMATTERS: Record<string, Intl.NumberFormat> = {};


/**
 * Get an formatter for this locale + currency.
 *
 * @param locale ISO 639
 * @param currency ISO 4217
 * @returns
 */
export function getFormatter(locale: string, currency: string): Intl.NumberFormat {
    currency = currency.toUpperCase();

    const key = locale + '|' + currency;
    let formatter = FORMATTERS[key];

    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
        });
        FORMATTERS[key] = formatter;
    }

    return formatter;
}


/**
 * Format integer money into a string.
 *
 * This adds a currency symbol, commas and applies an appropriate decimal point.
 *
 * E.g.
 *  - in AUD `123456` becomes `$1,234.56`
 *  - in JPY `123456` becomes `¥123,456`
 *
 * Note if the locale is `en-US`, then it formats AUD as `A$1,234.56`.
 *
 * @param value an _integer_ value
 * @param locale ISO 639
 * @param currency ISO 4217
 * @returns
 */
export function format(value: number, locale = 'en-AU', currency = 'AUD') {
    currency = currency.toUpperCase();

    if (typeof value !== 'number') {
        // @ts-ignore
        value = parseInt(value, 100);
    }

    // When it's NaN.
    if (value !== value) {
        value = 0;
    }

    // Do conversions.
    switch (currency) {
        default:
        case 'USD':
        case 'AUD':
            value /= 100;
            break;

        case 'JPY':
            break;
    }

    return getFormatter(locale, currency).format(value);
}


/**
 * Format integer money into a string, but also strips decimals when they're empty.
 *
 * Just makes things prettier I guess.
 *
 * E.g.
 * - `123400` becomes `$1,234`.
 * - `123456` becomes `$1,234.56`.
 *
 * @param value an _integer_ value
 * @param locale ISO 639
 * @param currency ISO 4217
 * @returns
 */
export function formatPretty(value: number, locale = 'en-AU', currency = 'AUD') {
    currency = currency.toUpperCase();

    let money = format(value, locale, currency);

    // Strip empty decimal places.
    switch (currency) {
        default:
        case 'USD':
        case 'AUD':
            if (value % 100 === 0) {
                money = money.slice(0, -3);
            }
            break;

        case 'JPY':
            break;
    }

    return money;
}


/**
 * Format integer money into a string without a currency symbol.
 *
 * This extends `formatPretty()` so empty decimals are removed. This also
 * strips anything that's not the number value, while retaining formatting.
 *
 * E.g.
 * - `123400` becomes `1,234`
 * - `123456` (USD, en-AU) becomes `1,234.56` (instead of US$1,234.56)
 *
 * @param value
 * @param locale
 * @param currency
 */
export function formatPrettyNoSymbol(value: number, locale = 'en-AU', currency = 'AUD') {
    let money = formatPretty(value, locale, currency);
    return money.replace(/[^0-9\-\., ]/g, '');
}
